import { z } from 'zod';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { labelCreateSchema, labelSchema } from '../types/entities/Label.js';
import { useLabelsStore } from '../stores/useLabelsStore.js';
const labelResponseSchema = z.object({
    labels: z.array(labelSchema),
});
const createLabelResponseSchema = z.object({
    labelId: z.string(),
});
export const labelUsageCountsSchema = z.object({
    tasks: z.number(),
    journals: z.number(),
    calendarEntries: z.number(),
    projects: z.number(),
    total: z.number(),
});
const labelUsageResponseSchema = z.object({
    usage: z.record(z.string(), labelUsageCountsSchema),
});
/**
 * Query keys the label DELETE now invalidates, because deleting a label also detaches it
 * from every entity that carried it server-side.
 *
 * invalidateQueries matches by prefix, so the bare keys cover the parameterised variants
 * (['journal', id], ['calendarEntries', params], and so on). ['labels'] also covers
 * ['labels', 'usage'].
 */
export const LABEL_DELETE_INVALIDATION_KEYS = [
    ['labels'],
    ['tasks'],
    ['journal'],
    ['projects'],
    ['calendarEntries'],
    ['calendarEntry'],
    ['calendarOccurrences'],
];
export const useGetLabels = (authenticatedRequest, autoFetch = true) => {
    const { setLabels } = useLabelsStore();
    return useQuery({
        queryKey: ['labels'],
        queryFn: async () => {
            const response = await authenticatedRequest({ url: '/labels', method: 'GET' }, labelResponseSchema);
            // Update the store when data is successfully fetched
            const labels = response?.data?.labels || [];
            setLabels(labels);
            return response;
        },
        enabled: autoFetch,
    });
};
/**
 * Usage counts for the Labels settings screen.
 *
 * Deliberately not written into useLabelsStore: the counts are display-only and
 * screen-local, and the store drives every label chip in the app, so putting them there
 * would re-render all of them on each refresh.
 *
 * Keyed under ['labels'] on purpose -- TanStack Query invalidates by key prefix, so the
 * existing invalidateQueries({ queryKey: ['labels'] }) in the create, update and remove
 * mutations refreshes the counts for free.
 */
export const useGetLabelUsage = (authenticatedRequest, autoFetch = true) => {
    return useQuery({
        queryKey: ['labels', 'usage'],
        queryFn: () => authenticatedRequest({ url: '/labels/usage', method: 'GET' }, labelUsageResponseSchema),
        enabled: autoFetch,
    });
};
export const useCreateLabel = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { addLabel, removeLabel } = useLabelsStore();
    return useMutation({
        mutationFn: (data) => {
            const validatedData = labelCreateSchema.parse(data);
            return authenticatedRequest({
                url: '/labels',
                method: 'POST',
                data: validatedData,
            }, createLabelResponseSchema);
        },
        onMutate: async (data) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['labels'],
            });
            // Optimistically add the label to the store
            const tempLabel = {
                id: `temp-${Date.now()}`, // Temporary ID
                ...data,
                createdAt: new Date(),
                updatedAt: new Date(),
            };
            addLabel(tempLabel);
            return { tempLabel };
        },
        onSuccess: (response, variables, context) => {
            // Remove the temporary label and invalidate to get the real data
            if (context?.tempLabel) {
                removeLabel(context.tempLabel.id);
            }
            // Invalidate queries to refetch with the actual server data
            queryClient.invalidateQueries({
                queryKey: ['labels'],
            });
        },
        onError: (error, variables, context) => {
            // Remove the optimistically added label on error
            if (context?.tempLabel) {
                removeLabel(context.tempLabel.id);
            }
        },
    });
};
export const useRemoveLabel = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { removeLabel, addLabel } = useLabelsStore();
    return useMutation({
        mutationFn: (labelId) => {
            return authenticatedRequest({
                url: `/labels/${labelId}`,
                method: 'DELETE',
            });
        },
        onMutate: async (labelId) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['labels'],
            });
            // Get the current labels data from the query cache
            const previousLabels = queryClient.getQueryData(['labels']);
            // Find the label to be removed for potential rollback
            const labelToRemove = previousLabels?.data?.labels?.find((label) => label.id === labelId);
            // Optimistically remove the label from the store
            removeLabel(labelId);
            return { labelToRemove, previousLabels };
        },
        onSuccess: () => {
            // The DELETE detaches the label from tasks, journals, calendar entries and projects
            // server-side, so every cached collection that can carry a label is now stale.
            for (const queryKey of LABEL_DELETE_INVALIDATION_KEYS) {
                queryClient.invalidateQueries({ queryKey });
            }
        },
        onError: (error, variables, context) => {
            // Rollback: re-add the label if the deletion failed
            if (context?.labelToRemove) {
                addLabel(context.labelToRemove);
            }
            // Optionally restore the entire previous state
            if (context?.previousLabels) {
                queryClient.setQueryData(['labels'], context.previousLabels);
            }
        },
    });
};
// Add this after useRemoveLabel
export const useUpdateLabel = (authenticatedRequest) => {
    const queryClient = useQueryClient();
    const { updateLabel } = useLabelsStore();
    return useMutation({
        mutationFn: ({ labelId, data }) => {
            const validatedData = labelCreateSchema.parse(data);
            return authenticatedRequest({
                url: `/labels/${labelId}`,
                method: 'PUT',
                data: validatedData,
            });
        },
        onMutate: async ({ labelId, data }) => {
            // Cancel any outgoing refetches
            await queryClient.cancelQueries({
                queryKey: ['labels'],
            });
            // Get the current labels data from the query cache
            const previousLabels = queryClient.getQueryData(['labels']);
            // Find the original label for potential rollback
            const originalLabel = previousLabels?.data?.labels?.find((label) => label.id === labelId);
            // Create the optimistically updated label
            const optimisticLabel = {
                ...originalLabel,
                ...data,
                id: labelId,
            };
            // Optimistically update the label in the store
            updateLabel(optimisticLabel);
            return { originalLabel, previousLabels };
        },
        onSuccess: (response, variables, context) => {
            // Invalidate queries to ensure we have the latest server data
            queryClient.invalidateQueries({
                queryKey: ['labels'],
            });
        },
        onError: (error, variables, context) => {
            // Rollback: restore the original label if the update failed
            if (context?.originalLabel) {
                updateLabel(context.originalLabel);
            }
            // Optionally restore the entire previous state
            if (context?.previousLabels) {
                queryClient.setQueryData(['labels'], context.previousLabels);
            }
        },
    });
};
